/**
 * ergonomicsAssessment.ts - Moduł oceny ergonomii ekspozytorów
 * PlexiSystem Engineering Module
 */

import {
  ERGO,
  validateOpenings,
  calculateOptimalShelfHeight,
  calculateShelfCount,
  checkVisibility
} from '../constants/ergonomics';

export interface ErgonomicsInput {
  use: 'fsdu' | 'counter' | 'infoStand' | 'shelf' | 'bin';
  dimensions: {
    height_cm: number;
    width_cm: number;
    depth_cm: number;
  };
  shelf_count?: number;
  shelf_heights_cm?: number[];
  hand_openings?: Array<{
    width_mm: number;
    height_mm: number;
    type: 'single_hand' | 'two_hands';
  }>;
  product_heights_cm?: number[];
  target_products?: Array<{
    name: string;
    importance: 'high' | 'medium' | 'low';
    height_cm: number;
  }>;
}

export interface ErgonomicsResult {
  overall_score: number; // 0-100
  visibility_analysis: Array<{
    shelf_number: number;
    height_cm: number;
    zone: string;
    visibility: 'excellent' | 'good' | 'fair' | 'poor';
    products?: string[];
  }>;
  opening_validation: Array<{
    opening_id: number;
    valid: boolean;
    issues?: string[];
  }>;
  recommendations: {
    shelf_adjustments?: Array<{
      shelf: number;
      current_height: number;
      recommended_height: number;
      reason: string;
    }>;
    opening_modifications?: string[];
    general?: string[];
  };
  golden_zone_usage: {
    percentage: number;
    products_in_zone: string[];
  };
  accessibility_score: number;
  warnings: string[];
}

/**
 * Główna funkcja oceny ergonomii
 */
export function assessErgonomics(input: ErgonomicsInput): ErgonomicsResult {
  const result: ErgonomicsResult = {
    overall_score: 100,
    visibility_analysis: [],
    opening_validation: [],
    recommendations: {
      shelf_adjustments: [],
      opening_modifications: [],
      general: []
    },
    golden_zone_usage: {
      percentage: 0,
      products_in_zone: []
    },
    accessibility_score: 100,
    warnings: []
  };
  
  // Analiza wysokości półek
  const shelfHeights = input.shelf_heights_cm || generateDefaultShelfHeights(
    input.dimensions.height_cm,
    input.shelf_count || 4
  );
  
  // Analiza widoczności dla każdej półki
  shelfHeights.forEach((height, index) => {
    const visibility = checkVisibility(height);
    result.visibility_analysis.push({
      shelf_number: index + 1,
      height_cm: height,
      zone: visibility.zone,
      visibility: visibility.visibility,
      products: getProductsAtShelf(input.target_products, height)
    });
    
    // Penalizuj za słabą widoczność
    if (visibility.visibility === 'poor') {
      result.overall_score -= 10;
    } else if (visibility.visibility === 'fair') {
      result.overall_score -= 5;
    }
  });
  
  // Sprawdź wykorzystanie golden zone
  const goldenZoneProducts = shelfHeights
    .filter(h => h >= ERGO.golden_zone_cm[0] && h <= ERGO.golden_zone_cm[1])
    .flatMap(h => getProductsAtShelf(input.target_products, h));
  
  result.golden_zone_usage = {
    percentage: (goldenZoneProducts.length / (input.target_products?.length || 1)) * 100,
    products_in_zone: goldenZoneProducts
  };
  
  // Walidacja otworów na dłonie
  if (input.hand_openings) {
    input.hand_openings.forEach((opening, index) => {
      const validation = validateOpenings(
        opening.width_mm,
        opening.height_mm,
        opening.type
      );
      
      result.opening_validation.push({
        opening_id: index + 1,
        valid: validation.valid,
        issues: validation.warnings
      });
      
      if (!validation.valid) {
        result.accessibility_score -= 20;
        result.opening_modifications?.push(...validation.warnings);
      }
    });
  }
  
  // Generuj rekomendacje dla półek
  if (input.target_products) {
    const optimizedLayout = optimizeShelfLayout(
      input.target_products,
      shelfHeights,
      input.dimensions.height_cm
    );
    
    optimizedLayout.forEach((opt, index) => {
      if (Math.abs(opt.recommended - shelfHeights[index]) > 5) {
        result.recommendations.shelf_adjustments?.push({
          shelf: index + 1,
          current_height: shelfHeights[index],
          recommended_height: opt.recommended,
          reason: opt.reason
        });
      }
    });
  }
  
  // Ogólne rekomendacje
  generateGeneralRecommendations(input, result);
  
  // Ostrzeżenia
  generateErgonomicWarnings(input, result);
  
  // Oblicz końcowy wynik
  result.overall_score = Math.max(0, Math.min(100, result.overall_score));
  
  return result;
}

/**
 * Generuje domyślne wysokości półek
 */
function generateDefaultShelfHeights(
  totalHeight_cm: number,
  shelfCount: number
): number[] {
  const heights: number[] = [];
  const spacing = totalHeight_cm / (shelfCount + 1);
  
  for (let i = 1; i <= shelfCount; i++) {
    heights.push(Math.round(spacing * i));
  }
  
  return heights;
}

/**
 * Pobiera produkty na danej wysokości
 */
function getProductsAtShelf(
  products?: Array<{ name: string; importance: string; height_cm: number }>,
  shelfHeight_cm?: number
): string[] {
  if (!products || !shelfHeight_cm) return [];
  
  return products
    .filter(p => Math.abs(p.height_cm - shelfHeight_cm) < 10)
    .map(p => p.name);
}

/**
 * Optymalizuje rozmieszczenie półek
 */
function optimizeShelfLayout(
  products: Array<{ name: string; importance: 'high' | 'medium' | 'low'; height_cm: number }>,
  currentHeights: number[],
  totalHeight: number
): Array<{ recommended: number; reason: string }> {
  const optimized: Array<{ recommended: number; reason: string }> = [];
  
  // Sortuj produkty według ważności
  const sortedProducts = [...products].sort((a, b) => {
    const importance = { high: 3, medium: 2, low: 1 };
    return importance[b.importance] - importance[a.importance];
  });
  
  // Przypisz najważniejsze produkty do golden zone
  currentHeights.forEach((height, index) => {
    if (index < sortedProducts.length) {
      const product = sortedProducts[index];
      
      if (product.importance === 'high') {
        // Najważniejsze w golden zone
        const targetHeight = (ERGO.golden_zone_cm[0] + ERGO.golden_zone_cm[1]) / 2;
        optimized.push({
          recommended: targetHeight,
          reason: `Produkt wysokiego priorytetu "${product.name}" w golden zone`
        });
      } else if (product.importance === 'medium') {
        // Średnie w strefie pochylenia
        const targetHeight = (ERGO.visibility_zones.stoop[0] + ERGO.visibility_zones.stoop[1]) / 2;
        optimized.push({
          recommended: targetHeight,
          reason: `Produkt średniego priorytetu "${product.name}" w strefie pochylenia`
        });
      } else {
        // Niskie na dole lub górze
        optimized.push({
          recommended: height, // Zostaw jak jest
          reason: 'Produkt niskiego priorytetu'
        });
      }
    } else {
      optimized.push({
        recommended: height,
        reason: 'Brak przypisanego produktu'
      });
    }
  });
  
  return optimized;
}

/**
 * Generuje ogólne rekomendacje
 */
function generateGeneralRecommendations(
  input: ErgonomicsInput,
  result: ErgonomicsResult
): void {
  // Rekomendacje dla typu ekspozytora
  switch (input.use) {
    case 'fsdu':
      if (input.dimensions.height_cm > 165) {
        result.recommendations.general?.push(
          'Wysokość FSDU > 165cm - rozważ obniżenie dla lepszej dostępności'
        );
      }
      break;
      
    case 'counter':
      if (input.dimensions.height_cm > 40) {
        result.recommendations.general?.push(
          'Ekspozytor blatowy > 40cm - może przesłaniać widok'
        );
      }
      break;
      
    case 'shelf':
      if (!input.shelf_count || input.shelf_count > 6) {
        result.recommendations.general?.push(
          'Rozważ 4-5 półek dla optymalnej ekspozycji'
        );
      }
      break;
  }
  
  // Rekomendacje dotyczące kątów nachylenia
  if (input.use === 'fsdu' || input.use === 'shelf') {
    result.recommendations.general?.push(
      `Zastosuj nachylenie półek ${ERGO.shelf_angle_deg.floor[0]}-${ERGO.shelf_angle_deg.floor[1]}° dla lepszej widoczności`
    );
  }
  
  // Rekomendacje dotyczące odstępów
  if (input.dimensions.depth_cm < 30) {
    result.recommendations.general?.push(
      'Głębokość < 30cm - produkty mogą być trudno dostępne'
    );
  }
  
  // Golden zone
  if (result.golden_zone_usage.percentage < 30) {
    result.recommendations.general?.push(
      'Zwiększ wykorzystanie golden zone (120-160cm) dla kluczowych produktów'
    );
  }
}

/**
 * Generuje ostrzeżenia ergonomiczne
 */
function generateErgonomicWarnings(
  input: ErgonomicsInput,
  result: ErgonomicsResult
): void {
  // Ostrzeżenia wysokości
  if (input.dimensions.height_cm > 200) {
    result.warnings.push('Wysokość > 200cm - górne półki będą niedostępne');
  }
  
  // Ostrzeżenia otworów
  if (input.hand_openings?.some(o => o.width_mm < 100)) {
    result.warnings.push('Niektóre otwory za wąskie dla komfortowego chwytania');
  }
  
  // Ostrzeżenia widoczności
  const poorVisibility = result.visibility_analysis.filter(v => v.visibility === 'poor');
  if (poorVisibility.length > 0) {
    result.warnings.push(
      `${poorVisibility.length} półek ma słabą widoczność`
    );
  }
  
  // Ostrzeżenia stabilności
  if (input.dimensions.depth_cm < input.dimensions.width_cm / 3) {
    result.warnings.push('Stosunek głębokości do szerokości może powodować niestabilność');
  }
}

/**
 * Oblicza optymalną liczbę produktów na półce
 */
export function calculateProductsPerShelf(
  shelfWidth_cm: number,
  productWidth_cm: number,
  productType: 'brochure' | 'box' | 'bottle'
): {
  count: number;
  spacing_cm: number;
  arrangement: string;
} {
  // Minimalne odstępy między produktami
  const minSpacing = ERGO.clearances_mm.between_products / 10; // Convert to cm
  
  // Oblicz ile produktów się zmieści
  const availableWidth = shelfWidth_cm - 2 * (ERGO.clearances_mm.from_edge / 10);
  const productWithSpacing = productWidth_cm + minSpacing;
  const count = Math.floor(availableWidth / productWithSpacing);
  
  // Oblicz rzeczywisty odstęp
  const totalProductWidth = count * productWidth_cm;
  const remainingSpace = availableWidth - totalProductWidth;
  const actualSpacing = remainingSpace / (count + 1);
  
  // Określ układ
  let arrangement = 'Pojedynczy rząd';
  if (productType === 'brochure' && count > 5) {
    arrangement = 'Kaskadowy dla lepszej widoczności';
  } else if (productType === 'bottle' && count > 8) {
    arrangement = 'Podwójny rząd z przesunięciem';
  }
  
  return {
    count,
    spacing_cm: Math.round(actualSpacing * 10) / 10,
    arrangement
  };
}
